import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, ArrowLeft, Settings, Check, Shield, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants';
import { Button } from '../components/shared/Button';

export const CookiePolicyPage: React.FC = () => {
    const { t } = useLanguage();

    const resetCookiePreferences = () => {
        localStorage.removeItem('ctc-cookie-consent');
        window.location.reload();
    };

    return (
        <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="mb-12">
                    <Link to={ROUTES.HOME} className="flex items-center text-gray-400 hover:text-white transition-colors group">
                        <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                        {t('common.back')}
                    </Link>
                </div>

                <div>
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-orange-500/10 mb-8 ring-1 ring-orange-500/20">
                            <Cookie size={40} className="text-orange-500" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black mb-6">{t('cookiePolicy.title')}</h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            {t('cookiePolicy.subtitle')}
                        </p>
                    </div>

                    <div className="space-y-8 text-gray-300 leading-relaxed">

                        {/* Last Updated */}
                        <div className="bg-surface-dark border border-gray-800 rounded-2xl p-6 text-sm text-gray-400">
                            <strong>{t('cookiePolicy.lastUpdated')}</strong> {t('cookiePolicy.lastUpdatedDate')}
                        </div>

                        {/* Important Notice */}
                        <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6">
                            <div className="flex items-start gap-4">
                                <Shield className="text-green-400 shrink-0 mt-1" size={24} />
                                <div>
                                    <h3 className="text-green-400 font-bold mb-2">{t('cookiePolicy.importantTitle')}</h3>
                                    <p className="text-sm">
                                        {t('cookiePolicy.importantP')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Intro */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8">
                            <h2 className="text-xl font-bold text-white mb-4">{t('cookiePolicy.whatTitle')}</h2>
                            <p className="mb-4">{t('cookiePolicy.whatP1')}</p>
                            <p>{t('cookiePolicy.whatP2')}</p>
                        </section>

                        {/* Technical Cookies */}
                        <section className="bg-surface-dark border border-green-500/30 rounded-3xl p-8 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-6 opacity-5">
                                <Settings size={80} />
                            </div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-500">
                                    <Check size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">{t('cookiePolicy.techTitle')}</h2>
                                    <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full border border-green-500/30">
                                        {t('cookiePolicy.techAlways')}
                                    </span>
                                </div>
                            </div>
                            <p className="text-gray-400 mb-6">{t('cookiePolicy.techDesc')}</p>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-700">
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('cookiePolicy.tableCol1')}</th>
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('cookiePolicy.tableCol2')}</th>
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('cookiePolicy.tableCol3')}</th>
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('cookiePolicy.tableCol4')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-b border-gray-800">
                                            <td className="py-3 px-4 font-mono text-xs text-blue-400">ctc-cookie-consent</td>
                                            <td className="py-3 px-4 text-gray-400">ctc-corp.eu</td>
                                            <td className="py-3 px-4 text-gray-400">{t('cookiePolicy.row1Duration')}</td>
                                            <td className="py-3 px-4">{t('cookiePolicy.row1Purpose')}</td>
                                        </tr>
                                        <tr className="border-b border-gray-800">
                                            <td className="py-3 px-4 font-mono text-xs text-blue-400">ctc_chatbot_session</td>
                                            <td className="py-3 px-4 text-gray-400">localStorage</td>
                                            <td className="py-3 px-4 text-gray-400">{t('cookiePolicy.row2Duration')}</td>
                                            <td className="py-3 px-4">{t('cookiePolicy.row2Purpose')}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* No Analytics */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-500/20 flex items-center justify-center text-gray-400">
                                    <AlertTriangle size={24} />
                                </div>
                                <h2 className="text-xl font-bold text-white">{t('cookiePolicy.analyticsTitle')}</h2>
                            </div>
                            <div className="bg-[#0B1120] rounded-xl p-6 text-center">
                                <p className="text-gray-400 mb-2">
                                    <strong className="text-green-400">{t('cookiePolicy.notUsed')}</strong>
                                </p>
                                <p className="text-sm text-gray-500">{t('cookiePolicy.analyticsDesc')}</p>
                            </div>
                        </section>

                        {/* No Marketing */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-500/20 flex items-center justify-center text-gray-400">
                                    <AlertTriangle size={24} />
                                </div>
                                <h2 className="text-xl font-bold text-white">{t('cookiePolicy.marketingTitle')}</h2>
                            </div>
                            <div className="bg-[#0B1120] rounded-xl p-6 text-center">
                                <p className="text-gray-400 mb-2">
                                    <strong className="text-green-400">{t('cookiePolicy.notUsed')}</strong>
                                </p>
                                <p className="text-sm text-gray-500">{t('cookiePolicy.marketingDesc')}</p>
                            </div>
                        </section>

                        {/* Third Party */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8">
                            <h2 className="text-xl font-bold text-white mb-4">{t('cookiePolicy.thirdPartyTitle')}</h2>
                            <p className="mb-4">{t('cookiePolicy.thirdPartyP')}</p>
                        </section>

                        {/* Manage Preferences */}
                        <section className="bg-surface-dark border border-blue-500/30 rounded-3xl p-8">
                            <h2 className="text-xl font-bold text-white mb-4">{t('cookiePolicy.prefsTitle')}</h2>
                            <p className="mb-6">{t('cookiePolicy.prefsP')}</p>

                            <div className="bg-[#0B1120] rounded-xl p-6 mb-6">
                                <h3 className="text-white font-semibold mb-3">{t('cookiePolicy.resetTitle')}</h3>
                                <p className="text-sm text-gray-400 mb-4">{t('cookiePolicy.resetDesc')}</p>
                                <Button
                                    onClick={resetCookiePreferences}
                                    variant="outline"
                                    className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
                                >
                                    <Settings size={16} className="mr-2" />
                                    {t('cookiePolicy.resetBtn')}
                                </Button>
                            </div>

                            <h3 className="text-white font-semibold mb-3">{t('cookiePolicy.browserTitle')}</h3>
                            <p className="text-sm text-gray-400 mb-4">{t('cookiePolicy.browserDesc')}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer"
                                    className="bg-[#0B1120] rounded-lg p-3 text-center hover:bg-[#151E32] transition-colors group">
                                    <span className="text-sm text-gray-400 group-hover:text-white flex items-center justify-center gap-1">
                                        Chrome <ExternalLink size={12} />
                                    </span>
                                </a>
                                <a href="https://support.mozilla.org/kb/cookies-information-websites-store" target="_blank" rel="noopener noreferrer"
                                    className="bg-[#0B1120] rounded-lg p-3 text-center hover:bg-[#151E32] transition-colors group">
                                    <span className="text-sm text-gray-400 group-hover:text-white flex items-center justify-center gap-1">
                                        Firefox <ExternalLink size={12} />
                                    </span>
                                </a>
                                <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471" target="_blank" rel="noopener noreferrer"
                                    className="bg-[#0B1120] rounded-lg p-3 text-center hover:bg-[#151E32] transition-colors group">
                                    <span className="text-sm text-gray-400 group-hover:text-white flex items-center justify-center gap-1">
                                        Safari <ExternalLink size={12} />
                                    </span>
                                </a>
                                <a href="https://support.microsoft.com/help/4027947" target="_blank" rel="noopener noreferrer"
                                    className="bg-[#0B1120] rounded-lg p-3 text-center hover:bg-[#151E32] transition-colors group">
                                    <span className="text-sm text-gray-400 group-hover:text-white flex items-center justify-center gap-1">
                                        Edge <ExternalLink size={12} />
                                    </span>
                                </a>
                            </div>
                        </section>

                        {/* Data Controller */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8">
                            <h2 className="text-xl font-bold text-white mb-4">{t('cookiePolicy.controllerTitle')}</h2>
                            <div className="bg-[#0B1120] rounded-xl p-4 space-y-2 text-sm">
                                <p><strong className="text-white">{t('cookiePolicy.controllerCompany')}:</strong> ChartTower Corp. OÜ</p>
                                <p><strong className="text-white">{t('cookiePolicy.controllerAddress')}:</strong> Ruunaoja 3, 11415 Tallinn, Estonia</p>
                                <p><strong className="text-white">Email:</strong> <a href="mailto:info@ctc-corp.eu" className="text-blue-400 hover:underline">info@ctc-corp.eu</a></p>
                                <p><strong className="text-white">{t('cookiePolicy.controllerDPO')}:</strong> Luca Marcialli</p>
                            </div>
                        </section>

                        {/* Link to Privacy Policy */}
                        <section className="bg-gradient-to-r from-blue-900/20 to-transparent border border-blue-500/30 rounded-3xl p-8">
                            <h2 className="text-xl font-bold text-white mb-4">{t('cookiePolicy.moreInfoTitle')}</h2>
                            <p className="mb-4">{t('cookiePolicy.moreInfoP')}</p>
                            <Link to={ROUTES.PRIVACY}>
                                <Button className="bg-blue-600 hover:bg-blue-500 text-white">
                                    <Shield size={16} className="mr-2" />
                                    {t('cookiePolicy.moreInfoBtn')}
                                </Button>
                            </Link>
                        </section>

                    </div>
                </div>
            </div>
        </div>
    );
};
